import { useClerk } from "@clerk/clerk-react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { attorneyNavItems, bottomAttorneyNavItems } from "../utils/attorneyNav";
import { AttorneyGetPayload } from "../../../../../api/src/types";
import MobileNav from "../utils/mobile/Nav";
import { PageLoading } from "../../Loading";
import ErrorMessage, { PageErrorContainer } from "../../Error";
import HelloprenupLogoWithText from "../../icons/HelloprenupLogoWithText";
import {
  DrawerItemGroup,
  NavBarRightItems,
  ScrollableNavItems,
  Link,
  DrawerHeader,
  NonScrollableNavItems,
} from "../utils/mobile/components";
import { CircleIconButton, IconButton } from "../../buttons/IconButton";
import MobileMenu from "../../icons/Menu";
import Drawer from "../utils/mobile/Drawer";
import CloseIcon from "../../icons/CloseIcon";
import { AttorneyLinkContainer } from "./components";
import DrawerItem from "../utils/mobile/DrawerItem";
import { COLOR_APP_TEXT } from "../../theme";
import { ParagraphHeaderSmall } from "../../typography/Paragraph";
import Divider from "../../Divider";
import { IconContainer } from "../utils/desktop/components";
import { SecondaryButtonSmall } from "../../buttons/SecondaryButton";
import Footer from "../Footer";
import WarningPopup from "../../popups/Warning";
import { useLocation } from "react-router";

const AttorneyMobileNav = ({
  attorney,
  isLoading,
  error,
}: {
  attorney?: AttorneyGetPayload | undefined;
  isLoading?: boolean;
  error?: boolean | FetchBaseQueryError | SerializedError;
}) => {
  const { signOut } = useClerk();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  if (isLoading) {
    return (
      <MobileNav>
        <PageLoading />
      </MobileNav>
    );
  }

  if (error) {
    return (
      <MobileNav>
        <PageErrorContainer>
          <ErrorMessage>Error occurred</ErrorMessage>
        </PageErrorContainer>
      </MobileNav>
    );
  }

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleLogout = () => {
    setIsDrawerOpen(false);
    setShowLogoutPopup(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutPopup(false);
    dispatch({ type: "USER_LOGOUT" });
    await signOut({ redirectUrl: "/login?attorney=true" });
  };

  const handleCancelLogout = () => {
    setShowLogoutPopup(false);
  };

  return (
    <>
      {showLogoutPopup && (
        <WarningPopup
          title="Are you sure you want to log out?"
          buttons={{
            confirmText: "Log out",
            cancelText: "Cancel",
            onConfirm: handleConfirmLogout,
            onCancel: handleCancelLogout,
          }}
        />
      )}
      <MobileNav>
        <HelloprenupLogoWithText />
        <NavBarRightItems>
          <IconButton onClick={toggleDrawer} data-testid="open-drawer">
            <MobileMenu />
          </IconButton>
        </NavBarRightItems>
        {isDrawerOpen && (
          <Drawer>
            <DrawerHeader>
              <HelloprenupLogoWithText />
              <CircleIconButton
                onClick={toggleDrawer}
                data-testid="close-drawer"
              >
                <CloseIcon />
              </CircleIconButton>
            </DrawerHeader>
            <ScrollableNavItems>
              {attorneyNavItems.map((item) => (
                <DrawerItemGroup key={item.label}>
                  <AttorneyLinkContainer
                    key={item.label}
                    isDisabled={
                      !attorney?.stage || !item.stage.includes(attorney.stage)
                    }
                  >
                    <Link
                      onClick={() => {
                        setIsDrawerOpen(false);
                      }}
                      to={item.path}
                      key={item.label}
                    >
                      <DrawerItem isActive={location.pathname === item.path}>
                        {item.icon}
                        <ParagraphHeaderSmall color={COLOR_APP_TEXT}>
                          {item.label}
                        </ParagraphHeaderSmall>
                      </DrawerItem>
                    </Link>
                  </AttorneyLinkContainer>
                </DrawerItemGroup>
              ))}
            </ScrollableNavItems>
            <Divider />
            <NonScrollableNavItems>
              {bottomAttorneyNavItems.map((item) => (
                <AttorneyLinkContainer
                  key={item.label}
                  isDisabled={
                    !attorney?.stage || !item.stage.includes(attorney.stage)
                  }
                >
                  <Link
                    onClick={() => {
                      setIsDrawerOpen(false);
                    }}
                    to={item.path}
                    key={item.label}
                  >
                    <DrawerItem isActive={location.pathname === item.path}>
                      <IconContainer>{item.icon}</IconContainer>
                      <ParagraphHeaderSmall color={COLOR_APP_TEXT}>
                        {item.label}
                      </ParagraphHeaderSmall>
                    </DrawerItem>
                  </Link>
                </AttorneyLinkContainer>
              ))}
              <SecondaryButtonSmall onClick={handleLogout}>
                Sign out
              </SecondaryButtonSmall>
              <Footer />
            </NonScrollableNavItems>
          </Drawer>
        )}
      </MobileNav>
    </>
  );
};

export default AttorneyMobileNav;
